import { type CSSProperties, type JSX, type ReactNode, useEffect, useRef } from "react";

interface RevealProps {
  children?: ReactNode;
  /** stamp = rows snapping in, rise = big type, draw = hairline drawing left-to-right */
  variant?: "stamp" | "rise" | "draw";
  delay?: number;
  className?: string;
}

/**
 * IntersectionObserver reveal. Adds `.is-in` once when the element enters the
 * viewport; all motion lives in index.css so reduced-motion kills it globally.
 */
export function Reveal({
  children,
  variant = "stamp",
  delay = 0,
  className = "",
}: RevealProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // No observer (an environment without IntersectionObserver): show the
    // content rather than leaving the reveal's hidden resting state in place.
    if (typeof IntersectionObserver !== "function") {
      el.classList.add("is-in");
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            el.classList.add("is-in");
            io.disconnect();
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const style: CSSProperties = delay ? { transitionDelay: `${delay}ms` } : {};
  return (
    <div ref={ref} className={`rv rv-${variant} ${className}`} style={style}>
      {children}
    </div>
  );
}
